import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { SearchspringRecommendProvider } from 'libs/searchspring/fetchers/recommend'
import { GridResults } from 'libs/searchspring/components/recommendations/GridResults'
import clsx from 'clsx'
import { BeaconAnalyticsProvider } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { parseJSON } from '@/components/helpers/utils'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'

// Helper component to track results state
const GridResultsWrapper = ({ profile, onResultsChange, children }) => {
  const { results, isLoading } = useSearchspringContext()

  // Notify parent when results/loading state changes
  useEffect(() => {
    onResultsChange(profile, {
      hasResults: results?.results?.length > 0,
      isLoading
    })
  }, [results, isLoading, profile, onResultsChange])

  return <>{children}</>
}

export function GridTabbed({ values, itemClass, productId, productCardSettings, titleContent, ctaContent, limit }) {
  const profiles = useMemo(() => values ? parseJSON(values) : [], [values])
  const parsedProductCardSettings = parseJSON(productCardSettings)
  const cardSettings = productCardSettings && Object.keys(parsedProductCardSettings).length > 0
    ? parsedProductCardSettings
    : {}

  const [activeProfile, setActiveProfile] = useState(profiles[0]?.profile)
  const [profileStates, setProfileStates] = useState({})

  // Callback to update profile states
  const handleResultsChange = useCallback((profile, state) => {
    setProfileStates(prev => ({
      ...prev,
      [profile]: state
    }))
  }, [])

  // Filter out profiles with no results
  const visibleProfiles = profiles?.filter(profile => {
    const state = profileStates[profile.profile]
    return !state || state.isLoading || state.hasResults
  })

  return (
    <div className="grid-main gap-x-1.5 gap-y-1.5">
      <div className="col-[1/-1] md:col-[1/-3] self-end flex flex-col md:flex-row md:items-end gap-y-1.25 pb-0.5">
        <div className="sticky left-0 flex items-end pr-1.5 empty:hidden" dangerouslySetInnerHTML={{ __html: titleContent }} />
        <div className="md-max:container-full flex items-end gap-x-1 overflow-x-scroll no-scrollbar overscroll-none whitespace-nowrap">
          {visibleProfiles.map((item) => (
            <button
              className={clsx(
                'w-fit paragraph-s capitalize border-b',
                item.profile === activeProfile
                  ? 'active border-b-black'
                  : 'text-neutral-primary border-b-transparent'
              )}
              key={item.profile}
              onClick={() => setActiveProfile(item.profile)}
              aria-label={item.label}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end col-span-full md:col-[-1/-3] md-max:row-[3] empty:hidden [&_.btn]:md-max:!w-full" dangerouslySetInnerHTML={{ __html: ctaContent }} />
      <div className="container-full col-span-full relative grid grid-cols-1 grid-rows-1 overflow-x-hidden">
        {profiles.map((item) => (
          <SearchspringRecommendProvider
            key={item.profile}
            profile={item.profile}
            productId={productId}
            limit={limit}
          >
            <BeaconAnalyticsProvider profile={item.profile}>
              <GridResultsWrapper
                profile={item.profile}
                onResultsChange={handleResultsChange}
              >
                <GridResults
                  className={itemClass}
                  data-dl-section-id={item.profile}
                  wrapperClassName={clsx(
                    'transition-opacity duration-150 row-start-1 col-start-1',
                    item.profile !== activeProfile
                      ? 'pointer-events-none touch-none opacity-0 delay-150'
                      : 'duration-300 z-1 opacity-100'
                  )}
                  productCardSettings={cardSettings}
                />
              </GridResultsWrapper>
            </BeaconAnalyticsProvider>
          </SearchspringRecommendProvider>
        ))}
      </div>
    </div>
  )
}
